import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import ViewOrderModal from "../components/vieworder";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function statusBadgeClasses(status) {
  switch (status) {
    case "Pending":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    case "At Sorting Hub":
      return "bg-blue-50 text-blue-700 ring-blue-200";
    case "In Transit":
      return "bg-sky-50 text-sky-700 ring-sky-200";
    case "At Delivery Hub":
      return "bg-indigo-50 text-indigo-700 ring-indigo-200";
    case "Out for Delivery":
      return "bg-purple-50 text-purple-700 ring-purple-200";
    case "On Hold":
      return "bg-yellow-50 text-yellow-700 ring-yellow-200";
    case "Delivered":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    case "Cancelled":
      return "bg-rose-50 text-rose-700 ring-rose-200";
    case "Returned":
      return "bg-rose-50 text-rose-700 ring-rose-200";
    default:
      return "bg-slate-50 text-slate-700 ring-slate-200";
  }
}

function paymentStatusClasses(status) {
  switch (status) {
    case "Paid":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    case "Unpaid":
      return "bg-rose-50 text-rose-700 ring-rose-200";
    case "Partially Paid":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    case "Refunded":
      return "bg-slate-50 text-slate-700 ring-slate-200";
    default:
      return "bg-slate-50 text-slate-700 ring-slate-200";
  }
}

export default function OrderManagementPage() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  async function fetchOrders() {
    try {
      const response = await axios.get(
        "https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/orders/get-all",
      );
      setOrders(response.data);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    }
  }

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch =
        order.merchant_order_id.toLowerCase().includes(search.toLowerCase()) ||
        order.recipient_name.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === "All" ? true : order.order_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, search, statusFilter]);

  const totalOrders = orders.length;
  const pendingCount = orders.filter(
    (o) => o.order_status === "Pending" || o.order_status === "Processing",
  ).length;

  const sortedCount = orders.filter((o) => o.order_status === "Sorted").length;

  const shippedCount = orders.filter(
    (o) =>
      o.order_status === "At Sorting Hub" ||
      o.order_status === "In Transit" ||
      o.order_status === "At Delivery Hub" ||
      o.order_status === "Out for Delivery" ||
      o.order_status === "On Hold",
  ).length;

  const deliveredCount = orders.filter(
    (o) => o.order_status === "Delivered",
  ).length;

  const cancelledCount = orders.filter(
    (o) => o.order_status === "Returned" || o.order_status === "Cancelled",
  ).length;

  async function handleCreateManualOrder(data) {
    const id = toast.loading("Creating manual order...");

    axios
      .post(
        "https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/order/create",
        data,
      )
      .then((response) => {
        toast.success("Manual order created successfully", { id });
        window.location.reload();
      })
      .catch((error) => {
        console.error("Failed to create manual order:", error);
        toast.error("Failed to create manual order", { id });
      });

    setIsManualModalOpen(false);
  }

  const handlePathaoSubmission = async (order) => {
    if (!order) {
      toast.error("Order not found");
      return;
    }
    const id = toast.loading("Submitting order to Pathao...");
    try {
      await axios.post(
        "https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/pathao/order/submit-to-pathao",
        { ...order, store_id: 162034 },
      );
      toast.success("Order submitted to Pathao successfully", { id });
      fetchOrders();
    } catch (error) {
      console.error("Failed to submit order to Pathao:", error);
      toast.error("Failed to submit order to Pathao", { id });
    }
  };

  const [selectedConsignment, setSelectedConsignment] = useState([]);

  const handlePrintMultipleLabels = async () => {
    if (!selectedConsignment || selectedConsignment.length === 0) {
      toast.error("No consignment IDs provided");
      return;
    }

    window.open(
      `https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/orders/print-label?consignment_ids=${selectedConsignment.join(
        ",",
      )}`,
    );
  };

  const handleCheckboxChange = (consignmentId) => {
    setSelectedConsignment((prevSelectedConsignment) =>
      prevSelectedConsignment.some((o) => o === consignmentId)
        ? prevSelectedConsignment.filter((o) => o !== consignmentId)
        : [...prevSelectedConsignment, consignmentId],
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <img
            src="https://aleeha-test.s3.ap-southeast-1.amazonaws.com/rr-2.png "
            alt="Retro Revive Logo"
            className="h-10 mb-4 rounded"
          />
          <h1 className="text-2xl font-semibold text-slate-900">
            Order Management
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            View, filter, and manage all customer orders in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50">
            Export CSV
          </button>
          <button
            className="inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
            onClick={() => setIsManualModalOpen(true)}
          >
            + Create Manual Order
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Total Orders" value={totalOrders} subtext="All time" />
        <StatCard
          label="Pending"
          value={pendingCount}
          subtext="Waiting for fulfillment"
        />
        <StatCard
          label="Sorted"
          value={sortedCount}
          subtext="Ready for shipment"
        />
        <StatCard
          label="Shipped"
          value={shippedCount}
          subtext="Handed over to courier"
        />
        <StatCard
          label="Delivered"
          value={deliveredCount}
          subtext="Completed orders"
        />
        <StatCard
          label="Cancelled"
          value={cancelledCount}
          subtext="Cancelled or refunded"
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-2">
          <div className="relative w-full sm:max-w-xs">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
              🔍
            </span>
            <input
              type="text"
              placeholder="Search by order ID or customer..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrintMultipleLabels}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Print Selected Labels ({selectedConsignment.length})
          </button>
        </div>

        <div className="flex items-center gap-2">
          <select
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Sorted">Sorted</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm text-slate-700">
            <thead className="bg-slate-50 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Order Status</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders?.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-sm text-slate-500"
                  >
                    No orders found. Try changing the filters.
                  </td>
                </tr>
              )}

              {filteredOrders?.map((order) => (
                <tr
                  key={order._id}
                  className="border-t border-slate-100 hover:bg-slate-50/60 text-center"
                >
                  <td>
                    <input
                      type="checkbox"
                      className="m-4"
                      checked={selectedConsignment.some(
                        (o) => o === order.consignment_id,
                      )}
                      onChange={() =>
                        handleCheckboxChange(order.consignment_id)
                      }
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900">
                        {order.merchant_order_id}
                      </span>
                      <span className="font-medium text-slate-900">
                        {order?.consignment_id || "Not Submitted"}
                      </span>
                      <span className={"text-xs text-slate-400"}>
                        {order.delivery_type === 48 ? "Regular" : "Express"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top ">
                    <div className="flex flex-col max-w-[200px] mx-auto">
                      <span className="text-sm font-medium text-slate-900">
                        {order.recipient_name}
                      </span>
                      <span className={"text-xs text-slate-400 flex-wrap"}>
                        {order.recipient_address}
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3 align-top">
                    <span className="text-sm text-slate-700">
                      {order.recipient_phone}
                    </span>
                  </td>

                  <td className="px-4 py-3 align-top">
                    <span className="text-sm text-slate-700">
                      {new Date(order.createdAt).toDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="text-sm text-slate-700">
                      {order.item_description.split(",").length}{" "}
                      {order.item_description.split(",").length > 1
                        ? "items"
                        : "item"}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="text-sm font-semibold text-slate-900">
                      {order.amount_to_collect.toLocaleString("en-BD")} BDT
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={classNames(
                        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
                        statusBadgeClasses(order.order_status),
                      )}
                    >
                      {order.order_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={classNames(
                        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
                        paymentStatusClasses(order.payment_status),
                      )}
                    >
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setIsViewModalOpen(true);
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        View
                      </button>

                      <button className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                        Update
                      </button>
                      <button
                        disabled={order.consignment_id}
                        onClick={() => handlePathaoSubmission(order)}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        {order.consignment_id
                          ? "Submitted"
                          : "Submit to Pathao"}
                      </button>
                      <button
                        disabled={
                          !order.consignment_id ||
                          order.order_status === "Delivered"
                        }
                        onClick={() =>
                          window.open(
                            `https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/order/print-label?consignment_id=${order.consignment_id}`,
                          )
                        }
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Print Label
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Order Modal */}
      <ManualOrderModal
        open={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        onSubmit={handleCreateManualOrder}
      />

      <ViewOrderModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        order={selectedOrder}
      />
    </div>
  );
}

function StatCard({ label, value, subtext }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{subtext}</p>
    </div>
  );
}

// Expected product shape example:
// product = {
//   pid: "p1",
//   title: "Rose Box",
//   variants: [{ sku:"RB-RED", img:"...", price:1200, title:"Red" }, ...]
// }

function ManualOrderModal({ open, onClose, onSubmit }) {
  const [query, setQuery] = useState("");
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const response = await axios.get(
          "https://ilm02zb64g.execute-api.ap-south-1.amazonaws.com/production/api/v1/products-get-all",
        );
        setAllProducts(response.data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      }
    }
    fetchProducts();
  }, []);

  const [form, setForm] = useState({
    merchant_order_id: "",
    recipient_name: "",
    recipient_phone: "",
    recipient_address: "",
    delivery_type: 48,
    item_type: 2,
    item_quantity: 1,
    item_weight: 0.5,
    payment_status: "Unpaid",
    trxid: "",
    amount_paid: 0,
    delivery_charge: 0,
    email: "",
    notes: "",
    item_description: "",
    gift_wrap: false,
  });

  // Selected items like checkout:
  // [{ pid, sku, title, variantTitle, img, price, quantity }]
  const [cart, setCart] = useState([]);

  // Reset when open/close (optional)
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCart([]);
  }, [open]);

  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allProducts.slice(0, 20);
    return allProducts
      .filter((p) => {
        const inTitle = (p?.title || "").toLowerCase().includes(q);
        const inSku = (p?.variants || []).some((v) =>
          (v?.sku || "").toLowerCase().includes(q),
        );
        return inTitle || inSku;
      })
      .slice(0, 30);
  }, [query, allProducts]);

  const subtotal = useMemo(() => {
    return cart.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.quantity),
      0,
    );
  }, [cart]);

  const orderTotal = useMemo(() => {
    return subtotal + Number(form.delivery_charge || 0);
  }, [subtotal, form.delivery_charge]);

  const amountToCollect = useMemo(() => {
    return Math.max(orderTotal - Number(form.amount_paid || 0), 0);
  }, [orderTotal, form.amount_paid]);

  function handleChange(e) {
    const { name, type, checked, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  }

  function addToCart(product, variant) {
    if (!variant?.sku) return;

    setCart((prev) => {
      const idx = prev.findIndex((x) => x.sku === variant.sku);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
        return copy;
      }
      return [
        ...prev,
        {
          pid: product.pid,
          sku: variant.sku,
          title: product.title,
          variant: variant.name,
          img: variant.img,
          price: Number(variant.price || 0),
          quantity: 1,
        },
      ];
    });
  }

  function updateQty(sku, qty) {
    const q = Number(qty);
    if (Number.isNaN(q) || q < 1) return;
    setCart((prev) =>
      prev.map((x) => (x.sku === sku ? { ...x, quantity: q } : x)),
    );
  }

  function removeItem(sku) {
    setCart((prev) => prev.filter((x) => x.sku !== sku));
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!form.recipient_name) {
      alert("Customer name is required.");
      return;
    }
    if (!form.recipient_phone) {
      alert("Phone number is required.");
      return;
    }
    if (!form.recipient_address) {
      alert("Delivery address is required.");
      return;
    }
    if (cart.length === 0) {
      alert("Please add at least 1 product.");
      return;
    }

    // Build payload matching your backend shape
    const payload = {
      ...form,
      products: cart.map((x) => ({
        pid: x.pid,
        sku: x.sku,
        title: x.title,
        variant: x.variant,
        img: x.img,
        price: x.price,
        quantity: x.quantity,
      })),
      order_total: orderTotal,
      amount_to_collect: amountToCollect,
      item_description: cart
        .map((p) => `${p.sku} [${p.variant} - ${p.quantity}Pc(s)]`)
        .join(", "),
      // You can also auto-generate item_description from cart if you want:
      // item_description: form.item_description || cart.map(x => `${x.title} (${x.variantTitle}) x${x.quantity}`).join(", "),
    };

    onSubmit(payload);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-6">
      <div className="relative w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Create Manual Order
            </h2>
            <p className="text-xs text-slate-500">
              Search products, add variants, then fill customer info.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="h-[calc(90vh-56px)]">
          <div className="grid h-full grid-cols-1 lg:grid-cols-2 overflow-hidden">
            {/* LEFT: Product selection */}
            <div className="h-full overflow-auto border-b lg:border-b-0 lg:border-r border-slate-200 p-5">
              {/* Search */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Search Product / SKU
                </label>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                  placeholder="Type product name or SKU…"
                />
              </div>

              {/* Results */}
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Products
                  </h3>
                  <span className="text-xs text-slate-500">
                    Showing {filteredProducts.length}
                  </span>
                </div>

                <div className="max-h-[320px] overflow-auto rounded-xl border border-slate-200">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500">
                      No products found.
                    </div>
                  ) : (
                    filteredProducts.map((p) => (
                      <ProductRow key={p.pid} product={p} onAdd={addToCart} />
                    ))
                  )}
                </div>
              </div>

              {/* Selected items (checkout-like) */}
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-slate-900">
                  Selected Items
                </h3>

                <div className="mt-2 rounded-xl border border-slate-200">
                  {cart.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500">
                      No items added yet.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {cart.map((item) => (
                        <div key={item.sku} className="flex gap-3 p-3">
                          <div className="h-12 w-12 overflow-hidden rounded-lg bg-slate-100">
                            {item.img ? (
                              <img
                                src={item.img}
                                alt={item.title}
                                className="h-full w-full object-cover"
                              />
                            ) : null}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">
                                  {item.title}
                                </p>
                                <p className="text-xs text-slate-500">
                                  SKU: {item.sku} [{item.variant}]
                                </p>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  removeItem(item.sku);
                                  if (item.sku === "GIFT-WRAP") {
                                    setForm((prev) => ({
                                      ...prev,
                                      gift_wrap: false,
                                    }));
                                  }
                                }}
                                className="rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                              >
                                Remove
                              </button>
                            </div>

                            <div className="mt-2 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">
                                  Qty
                                </span>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    updateQty(item.sku, e.target.value)
                                  }
                                  className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                                />
                              </div>

                              <div className="text-right">
                                <p className="text-sm font-semibold text-slate-900">
                                  ৳
                                  {Number(item.price * item.quantity).toFixed(
                                    0,
                                  )}
                                </p>
                                <p className="text-xs text-slate-500">
                                  ৳{Number(item.price).toFixed(0)} each
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div className="mt-3 rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-semibold text-slate-900">
                      ৳{subtotal.toFixed(0)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Delivery</span>
                    <span className="font-semibold text-slate-900">
                      ৳{Number(form.delivery_charge || 0).toFixed(0)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Total</span>
                    <span className="font-semibold text-slate-900">
                      ৳{orderTotal.toFixed(0)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Due (Collect)</span>
                    <span className="font-semibold text-slate-900">
                      ৳{amountToCollect.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Order form */}
            <div className="h-full overflow-auto p-5">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Customer Name<span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="recipient_name"
                    value={form.recipient_name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    placeholder="e.g. Md. Faysal Jamil Nadim"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Phone Number<span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="recipient_phone"
                    value={form.recipient_phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    placeholder="e.g. +8801XXXXXXXXX"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Delivery Address<span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="recipient_address"
                    value={form.recipient_address}
                    onChange={handleChange}
                    className="w-full min-h-[78px] rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    placeholder="e.g. House #123, Road #45, Gulshan, Dhaka"
                  />
                </div>

                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="gift_wrap"
                      checked={form.gift_wrap}
                      // onChange={handleChange}
                      onChange={(e) => {
                        setForm({ ...form, gift_wrap: e.target.checked });
                        if (!e.target.checked) {
                          // Remove gift wrap from cart
                          setCart((prev) =>
                            prev.filter((x) => x.pid !== "gift-wrap"),
                          );
                          return;
                        }
                        addToCart(
                          {
                            pid: "gift-wrap",
                            title: "Gift Wrapping",
                          },
                          {
                            sku: "GIFT-WRAP",
                            name: "Standard Gift Wrap",
                            img: "https://retrorevive.s3.ap-southeast-1.amazonaws.com/GIFT_WRAP.jpg",
                            price: 100,
                          },
                        );
                      }}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            Gift Wrapping
                          </p>
                          <p className="text-xs text-slate-500">
                            Add wrapping to make your gift extra special.
                          </p>
                        </div>
                      </div>

                      {/* Optional note */}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Notes / Item Description
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    className="w-full min-h-[78px] rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    placeholder="Optional notes (gift wrap, color request, etc.)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Order ID<span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="merchant_order_id"
                      value={form.merchant_order_id}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="e.g. 1001"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Payment Status
                    </label>
                    <select
                      name="payment_status"
                      value={form.payment_status}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    >
                      <option value="Unpaid">Unpaid</option>
                      <option value="Paid">Paid</option>
                      <option value="Partially Paid">Partially Paid</option>
                      <option value="Refunded">Refunded</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Delivery Charge (BDT)
                    </label>
                    <input
                      type="number"
                      name="delivery_charge"
                      value={form.delivery_charge}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="e.g. 110"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Amount Paid (BDT)
                    </label>
                    <input
                      type="number"
                      name="amount_paid"
                      value={form.amount_paid}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="e.g. 500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      name="item_weight"
                      value={form.item_weight}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="e.g. 0.5"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Delivery Type
                    </label>
                    <select
                      name="delivery_type"
                      value={form.delivery_type}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    >
                      <option value={48}>Regular</option>
                      <option value={12}>On Demand</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      TRX ID
                    </label>
                    <input
                      type="text"
                      name="trxid"
                      value={form.trxid}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="Optional"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-slate-700">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {/* Read-only totals preview */}
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Order Total</span>
                    <span className="font-semibold text-slate-900">
                      ৳{orderTotal.toFixed(0)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Amount To Collect</span>
                    <span className="font-semibold text-slate-900">
                      ৳{amountToCollect.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer buttons */}
              <div className="sticky bottom-0 mt-5 bg-white pt-3 border-t border-slate-200">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
                  >
                    Save Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProductRow({ product, onAdd }) {
  const [selectedSku, setSelectedSku] = useState("");

  useEffect(() => {
    // Default to first variant if exists
    const first = product?.variants?.[0]?.sku || "";
    setSelectedSku(first);
  }, [product?.pid]);

  const selectedVariant = useMemo(() => {
    return (
      (product.variants || []).find((v) => v.sku === selectedSku) ||
      product.variants?.[0]
    );
  }, [selectedSku, product.variants]);

  return (
    <div className="flex items-center justify-between gap-3 p-3 hover:bg-slate-50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">
          {product.title}
        </p>
        <div className="mt-1 flex items-center gap-2">
          <select
            value={selectedSku}
            onChange={(e) => setSelectedSku(e.target.value)}
            className="w-full max-w-[280px] rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
          >
            {(product.variants || []).map((v) => (
              <option key={v.sku} value={v.sku}>
                {v.sku} [{v.name}]
              </option>
            ))}
          </select>
        </div>
        {selectedVariant ? (
          <p className="mt-1 text-xs text-slate-500">
            Price: ৳{Number(selectedVariant.price || 0).toFixed(0)} • Stock:{" "}
            {selectedVariant.stock || 0} Pc(s)
          </p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => onAdd(product, selectedVariant)}
        disabled={!selectedVariant?.stock || selectedVariant.stock <= 0}
        className="shrink-0 disabled:opacity-50 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
      >
        Add
      </button>
    </div>
  );
}
